import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { adminApi } from '../api'
import {
  adminFixtureAccounts,
  adminFixtureClients,
  adminFixtureDeposits,
  adminFixtureTimeline,
  useAdminFixturesFromQuery,
} from '../fixtures'
import type { AdminClient, AdminDeposit, AdminTradingAccount } from '../types'
import { money } from '../../client/prediction-filter'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../components/ConfirmDialog'
import {
  eventDescription,
  eventTimestamp,
  formatWhen,
  maskLogin,
  safeAdminError,
} from '../safe-ui'

type Tab =
  | 'profile'
  | 'accounts'
  | 'deposits'
  | 'withdrawals'
  | 'transactions'
  | 'timeline'
  | 'audit'

const TABS: { id: Tab; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'accounts', label: 'Trading Accounts' },
  { id: 'deposits', label: 'Deposits' },
  { id: 'withdrawals', label: 'Withdrawals' },
  { id: 'transactions', label: 'Transactions' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'audit', label: 'Audit' },
]

function accountLoginMasked(a: AdminTradingAccount): string {
  if (a.mt5_login_masked) return a.mt5_login_masked
  return maskLogin(a.mt5_login ?? a.mt5Login)
}

export function AdminClientDetailPage() {
  const { id } = useParams()
  const clientId = Number(id)
  const fixtures = useAdminFixturesFromQuery()
  const [tab, setTab] = useState<Tab>('profile')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [client, setClient] = useState<(AdminClient & Record<string, unknown>) | null>(null)
  const [accounts, setAccounts] = useState<AdminTradingAccount[]>([])
  const [events, setEvents] = useState<Array<Record<string, unknown>>>([])
  const [deposits, setDeposits] = useState<AdminDeposit[]>([])
  const [busy, setBusy] = useState(false)
  const [confirmAction, setConfirmAction] = useState<'SUSPEND' | 'REACTIVATE' | null>(null)

  async function load() {
    if (!Number.isFinite(clientId)) {
      setError('Invalid client id')
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      if (fixtures) {
        const found = adminFixtureClients.find((c) => c.id === clientId) || adminFixtureClients[0]
        setClient(found)
        setAccounts(adminFixtureAccounts.filter((a) => a.client_id === found.id))
        setEvents(adminFixtureTimeline as Array<Record<string, unknown>>)
        setDeposits(adminFixtureDeposits.filter((d) => d.client_id === found.id))
        return
      }
      const [c, a, t, d] = await Promise.all([
        adminApi.getClient(clientId),
        adminApi.clientAccounts(clientId),
        adminApi.clientTimeline(clientId).catch(() => ({ events: [] as Array<Record<string, unknown>> })),
        adminApi.listDeposits({ clientId, page: 1, pageSize: 50 }).catch(() => ({ deposits: [] as AdminDeposit[] })),
      ])
      setClient(c.client)
      setAccounts(a.accounts || [])
      setEvents(t.events || [])
      setDeposits(d.deposits || [])
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load client'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, fixtures])

  async function applyStatus(action: 'SUSPEND' | 'REACTIVATE', reason: string) {
    if (fixtures) return
    setBusy(true)
    setConfirmAction(null)
    try {
      await adminApi.setClientStatus(clientId, action, reason)
      await load()
    } catch (err) {
      setError(safeAdminError(err, 'Status update failed'))
    } finally {
      setBusy(false)
    }
  }

  const q = fixtures ? '?fixtures=1' : ''

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Client detail" title={client?.display_name || `Client #${clientId}`}>
        <p className="cp-muted">
          <Link to={`/admin/clients${q}`}>← Clients</Link>
        </p>
      </PageHeader>

      {fixtures ? (
        <StateBlock tone="warn" title="Fixture mode · visual review only">
          Status actions are disabled in fixture preview.
        </StateBlock>
      ) : null}

      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}
      {error ? <p className="cp-error">{error}</p> : null}

      <div className="ap-tabs" role="tablist" aria-label="Client sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`ap-tab${tab === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' ? (
        <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
          <h2>Profile</h2>
          <dl className="cp-stat-grid">
            <div>
              <dt>ID</dt>
              <dd>{client?.id ?? clientId}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{client ? String(client.email || '—') : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{client ? String(client.status || '—') : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Verified</dt>
              <dd>{client ? (client.email_verified ? 'Yes' : 'No') : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>{client ? String(client.phone || '—') : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Country</dt>
              <dd>{client ? String(client.country || '—') : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Registered</dt>
              <dd>{client ? formatWhen(client.registered_at) : loading ? 'Loading…' : '—'}</dd>
            </div>
            <div>
              <dt>Last login</dt>
              <dd>{client ? formatWhen(client.last_login_at) : loading ? 'Loading…' : '—'}</dd>
            </div>
          </dl>
          {client ? (
            <div className="cp-platform-actions">
              <Button
                variant="secondary"
                disabled={busy || fixtures || String(client.status).toUpperCase() === 'SUSPENDED'}
                onClick={() => setConfirmAction('SUSPEND')}
              >
                Suspend
              </Button>
              <Button
                variant="secondary"
                disabled={busy || fixtures || String(client.status).toUpperCase() === 'ACTIVE'}
                onClick={() => setConfirmAction('REACTIVATE')}
              >
                Reactivate
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === 'accounts' ? (
        <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
          <h2>Trading accounts</h2>
          <ResponsiveTable
            rows={accounts}
            rowKey={(a) => String(a.id)}
            empty={loading ? 'Loading…' : 'No trading accounts.'}
                columns={[
                  { key: 'id', header: 'ID', render: (a) => a.id },
                  {
                    key: 'login',
                    header: 'MT5 login',
                    render: (a) => accountLoginMasked(a),
                  },
                  { key: 'server', header: 'Server', render: (a) => a.server || '—' },
                  { key: 'mode', header: 'Mode', render: (a) => a.mode || '—' },
                  { key: 'status', header: 'Status', render: (a) => a.status || '—' },
                  { key: 'balance', header: 'Balance', render: (a) => money(a.balance) },
                  {
                    key: 'open',
                    header: 'Open',
                    render: (a) => (
                      <Link
                        className="pp-btn pp-btn--secondary pp-btn--sm"
                        to={`/admin/trading-accounts/${a.id}${q}`}
                      >
                        Open
                      </Link>
                    ),
                  },
                ]}
              />
              <p className="cp-note">
                Leverage, group, and passwords are never displayed.
              </p>
        </div>
      ) : null}

      {tab === 'deposits' ? (
        <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
          <h2>Deposits</h2>
          <ResponsiveTable
            rows={deposits}
            rowKey={(d) => String(d.id)}
            empty={loading ? 'Loading…' : 'No deposits for this client.'}
                columns={[
                  { key: 'ref', header: 'Reference', render: (d) => d.reference || '—' },
                  {
                    key: 'amount',
                    header: 'Amount',
                    render: (d) => `${money(d.amount)} ${d.currency || 'USD'}`,
                  },
                  { key: 'provider', header: 'Provider', render: (d) => d.provider || '—' },
                  { key: 'status', header: 'Status', render: (d) => d.status || '—' },
                  { key: 'created', header: 'Created', render: (d) => formatWhen(d.created_at) },
                  {
                    key: 'open',
                    header: '',
                    render: () => (
                      <Link className="pp-btn pp-btn--secondary pp-btn--sm" to={`/admin/deposits${q}`}>
                        Queue
                      </Link>
                    ),
                  },
              ]}
            />
        </div>
      ) : null}

      {tab === 'withdrawals' ? (
        <div className="cp-panel">
          <h2>Withdrawals</h2>
          <StateBlock tone="warn" title="Not available via admin API">
            Withdrawal operations are not available in Admin yet. Debit/refund backend is incomplete.
          </StateBlock>
        </div>
      ) : null}

      {tab === 'transactions' ? (
        <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
          <h2>Transactions</h2>
          <StateBlock tone="info">
            Currently deposit-derived records only for this client.
          </StateBlock>
          <ResponsiveTable
            rows={deposits}
            rowKey={(d) => String(d.id)}
            empty={loading ? 'Loading…' : 'No deposit-derived transactions.'}
                columns={[
                  { key: 'ref', header: 'Reference', render: (d) => d.reference || '—' },
                  { key: 'type', header: 'Type', render: () => 'Deposit' },
                  {
                    key: 'amount',
                    header: 'Amount',
                    render: (d) => `${money(d.amount)} ${d.currency || 'USD'}`,
                  },
                  { key: 'provider', header: 'Provider', render: (d) => d.provider || '—' },
                  { key: 'status', header: 'Status', render: (d) => d.status || '—' },
                  { key: 'created', header: 'Created', render: (d) => formatWhen(d.created_at) },
              ]}
            />
        </div>
      ) : null}

      {tab === 'timeline' || tab === 'audit' ? (
        <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
          <h2>{tab === 'timeline' ? 'Timeline' : 'Audit'}</h2>
          {tab === 'audit' ? (
            <StateBlock tone="info">
              Audit events for this client come from the same timeline API. There is no separate global audit feed
              on this page.
            </StateBlock>
          ) : null}
          {loading && events.length === 0 ? (
            <p className="cp-muted">Loading…</p>
          ) : events.length === 0 ? (
            <p className="cp-muted">No timeline events.</p>
          ) : (
            <div className="cp-card-list">
              {events.slice(0, 50).map((ev, i) => (
                <article key={i} className="cp-row-card">
                  <div>
                    <strong>{String(ev.event_type || ev.type || 'Event')}</strong>
                    <span className="cp-muted">{formatWhen(eventTimestamp(ev))}</span>
                  </div>
                  <div className="cp-muted">{eventDescription(ev)}</div>
                  {ev.mt5_login_masked || ev.account_id ? (
                    <div className="cp-muted">
                      {ev.account_id ? `Account #${String(ev.account_id)}` : ''}
                      {ev.mt5_login_masked
                        ? ` · Login ${maskLogin(String(ev.mt5_login_masked))}`
                        : ''}
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {!loading && !client && !error ? (
        <StateBlock tone="warn" title="Not found">
          Client record unavailable.
        </StateBlock>
      ) : null}

      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmAction === 'SUSPEND' ? 'Suspend client?' : 'Reactivate client?'}
        body={
          <p>
            This updates client #{clientId} via the existing admin status API. Provide a reason to continue.
          </p>
        }
        confirmLabel={confirmAction || 'Confirm'}
        danger={confirmAction === 'SUSPEND'}
        busy={busy}
        requireReason
        onCancel={() => setConfirmAction(null)}
        onConfirm={(reason) => {
          if (confirmAction) void applyStatus(confirmAction, reason)
        }}
      />
    </section>
  )
}
